import { useEffect, useRef, useState } from "react";
import { Pencil } from "lucide-react";
import { cn } from "../../lib/cn";

export interface InlineEditProps {
  value: string;
  onSave: (next: string) => void | Promise<void>;
  /** Rendered when the field is empty, e.g. "Add a name". */
  placeholder?: string;
  /** Shown in view mode instead of the raw value — lets callers highlight search matches. */
  children?: React.ReactNode;
  ariaLabel: string;
  className?: string;
}

/**
 * Click-to-edit for a single low-risk text field, so changing one name does not require opening
 * the full modal.
 *
 * The full modal is deliberately left in place for everything this cannot express (tags, opt-out
 * state, phone number). This is a shortcut, not a replacement.
 *
 * Save-on-blur plus save-on-Enter, cancel-on-Escape. The `committed` guard matters: Enter saves
 * and then moves focus off the input, which fires blur — without it the same edit would be sent
 * twice, producing two identical PATCHes for one keystroke.
 */
export function InlineEdit({
  value,
  onSave,
  placeholder = "—",
  children,
  ariaLabel,
  className,
}: InlineEditProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const committed = useRef(false);

  // Re-sync when the row's value changes underneath (a refetch, or an edit from the modal).
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (!editing) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editing]);

  function commit() {
    if (committed.current) return;
    committed.current = true;
    const next = draft.trim();
    setEditing(false);
    // Nothing changed — skip the network call entirely rather than PATCHing an identical value.
    if (next !== value.trim()) void onSave(next);
  }

  function cancel() {
    committed.current = true;
    setDraft(value);
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        aria-label={ariaLabel}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
        }}
        // Stops a click inside the input from reaching a row-level onRowPress handler.
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "h-8 w-full min-w-0 rounded-control border border-accent bg-surface px-2 text-sm text-text",
          "outline-none ring-2 ring-accent/20",
          className,
        )}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        committed.current = false;
        setEditing(true);
      }}
      aria-label={`${ariaLabel}. Click to edit.`}
      className={cn(
        "group/inline -mx-1 flex w-full min-w-0 items-center gap-1.5 rounded-control px-1 py-0.5 text-left",
        "transition-colors duration-fast ease-standard hover:bg-surface-hover",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
        className,
      )}
    >
      <span className={cn("truncate", !value && "text-text-muted")}>
        {children ?? (value || placeholder)}
      </span>
      {/* Only on hover/focus: a pencil on every row would turn the column into visual noise. */}
      <Pencil
        aria-hidden
        className="size-3 shrink-0 text-text-muted opacity-0 transition-opacity duration-fast group-hover/inline:opacity-100 group-focus-visible/inline:opacity-100"
      />
    </button>
  );
}
